// © Microsoft Corporation. All rights reserved.

import { getAppEnvironment, getHeadData } from "@msnews/core";
import { useEnterpriseCompliance, useEnterpriseDomain } from "@msnews/auth";

import { logger } from "@msnews/diagnostics";

/**
 * The service URL to use
 */
let serviceUrl: URL;

/**
 * The feed service URL to use
 */
let feedServiceUrl: URL;

/**
 * Initialize AppServiceManager with serviceUrl
 *
 * @param url {string} - The service url
 */
export function initializeServiceRequest(url: URL): void {
    serviceUrl = new URL("", url);
    initializationLogs(serviceUrl);
}

/**
 * Initialize AppServiceManager with feedServiceUrl
 *
 * @param url {string} - The feed service url
 */
export function initializeFeedServiceRequest(url: URL): void {
    feedServiceUrl = new URL("", url);
    initializationLogs(feedServiceUrl);
}

/**
 * Returns service url
 * @returns an URL pointing to Service Request base URL
 */
export function getServiceUrl(): URL {
    const appEnvironment = getAppEnvironment();

    if (!serviceUrl) {
        // Use enterprise request if in enterprise compliant scenario
        const isEnterpriseDomain = useEnterpriseCompliance() && useEnterpriseDomain();
        const url = isEnterpriseDomain ? appEnvironment.EnterpriseServiceUrlBase : appEnvironment.ServiceUrlBase;

        initializeServiceRequest(url);
    }

    return serviceUrl;
}

/**
 * Returns feed service url
 * @returns an URL pointing to Feed Service Request base URL
 */
export function getFeedServiceUrl(): URL {
    const appEnvironment = getAppEnvironment();

    if (!feedServiceUrl) {
        // Use enterprise request if in enterprise compliant scenario
        const isEnterpriseDomain = useEnterpriseCompliance() && useEnterpriseDomain();
        const url = isEnterpriseDomain ? appEnvironment.EnterpriseServiceUrlBase : appEnvironment.FeedServiceUrlBase;

        initializeFeedServiceRequest(url);
    }

    return feedServiceUrl;
}

/**
 * Appends `subPath` to Service Request URL.
 * It is an abstraction for the client so it won't have to
 * know the url format
 * @param subPath the path to append to Service Request URL
 * @param serviceUrl optional Service URL override
 * @returns an URL pointing to Service Request base URL appended by `subPath`
 */
export function getServiceUrlForSubpath(subPath: string, serviceUrl: URL = getServiceUrl()): URL {
    return new URL(subPath + serviceUrl?.search, serviceUrl);
}

/**
 * Appends `subPath` to Feed Service Request URL.
 * It is an abstraction for the client so it won't have to
 * know the url format
 * @param subPath the path to append to Feed Service Request URL
 * @param feedServiceUrl optional Feed Service URL override
 * @returns an URL pointing to Feed Service Request base URL appended by `subPath`
 */
export function getFeedServiceUrlForSubpath(subPath: string, feedServiceUrl: URL = getFeedServiceUrl()): URL {
    return new URL(subPath + feedServiceUrl?.search, feedServiceUrl);
}

/**
 * Add initial logs based on url and environment
 * @param url the url that is being used to initialize
 */
function initializationLogs(url: URL): void {
    let authEnabled = false;
    if (!process.env.WEBPACK_DEV_SERVER && url.searchParams.get("env.service") !== "dev" && getHeadData().UserIsSignedIn) {
        authEnabled = true;
    }

    logger.log("u-si: " + getAppEnvironment().UserId);
    logger.log("a-si: " + authEnabled);
    logger.log("ss-si: " + getHeadData().UserIsSignedIn);
}